import "../scss/components/filter-drawer.scss";

const FILTER_DEBOUNCE_MS = 300;

class FilterDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawerDesktop = null;
    this.drawerMobile = null;
    this.productGrid = null;
    this.isDesktop = window.innerWidth >= 990;
    this.abortController = null;
    this.applyFiltersTimer = null;
  }

  connectedCallback() {
    this.drawerDesktop = this.querySelector("#filterDrawer");
    this.drawerMobile = this.querySelector("#filterDrawerMobile");
    this.productGridWrapper = this.querySelector("#productGridWrapper");
    this.productGrid = this.querySelector("#ProductGridContainer");

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.updateFilterCount();

    // Set select width immediately and also after a frame to ensure it's applied
    this.setSelectDynamicWidth();
    requestAnimationFrame(() => this.setSelectDynamicWidth());

    window.addEventListener("resize", () => {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.innerWidth >= 1024;
      if (wasDesktop !== this.isDesktop) {
        this.drawerDesktop?.hide();
        this.drawerMobile?.hide();
      }
    });

    window.addEventListener("popstate", (event) => {
      const searchParams =
        event.state?.searchParams || window.location.search.slice(1);
      this.renderPage(searchParams, false);
    });

    // Reveal the filter drawers now that initialization is complete
    this.drawerDesktop?.classList.remove("hidden");
    this.drawerMobile?.classList.remove("hidden");
  }

  setupEventListeners() {
    const toggleBtn = this.querySelector('[data-filter-toggle="desktop"]');
    const toggleBtnMobile = this.querySelector('[data-filter-toggle="mobile"]');
    const sortSelect = this.querySelector("[data-sort-select]");
    const applyBtn = this.querySelector("[data-apply-filters]");
    const clearBtnDesktop = this.querySelector(
      '[data-clear-filters="desktop"]',
    );
    const clearBtnMobile = this.querySelector(
      "#filterDrawerMobile [data-clear-filters]",
    );
    const closeMobileBtn = this.querySelector("[data-close-mobile-drawer]");

    toggleBtn?.addEventListener("click", () => this.toggleDrawer("desktop"));
    toggleBtnMobile?.addEventListener("click", () =>
      this.toggleDrawer("mobile"),
    );
    closeMobileBtn?.addEventListener("click", () => this.drawerMobile?.hide());

    this.drawerDesktop?.addEventListener("sl-show", () =>
      this.onDrawerToggle("desktop", true),
    );
    this.drawerDesktop?.addEventListener("sl-hide", () =>
      this.onDrawerToggle("desktop", false),
    );
    this.drawerMobile?.addEventListener("sl-show", () =>
      this.onDrawerToggle("mobile", true),
    );
    this.drawerMobile?.addEventListener("sl-hide", () =>
      this.onDrawerToggle("mobile", false),
    );

    sortSelect?.addEventListener("change", (e) => {
      this.handleSortChange(e.target.value);
      this.setSelectDynamicWidth();
    });
    applyBtn?.addEventListener("click", () => {
      this.applyFilters();
      this.drawerMobile?.hide();
    });

    clearBtnDesktop?.addEventListener("click", () => this.clearAllFilters());
    clearBtnMobile?.addEventListener("click", () => {
      this.clearAllFilters();
      if (!this.isDesktop) this.drawerMobile?.hide();
    });

    this.attachFilterListeners();
  }

  attachFilterListeners() {
    const accordionButtons = this.querySelectorAll("[data-accordion-toggle]");
    const filterInputs = this.querySelectorAll("[data-filter-input]");

    accordionButtons.forEach((button) => {
      button.addEventListener("click", (e) =>
        this.toggleAccordion(e.currentTarget),
      );
    });

    filterInputs.forEach((input) => {
      input.addEventListener("change", () => {
        if (this.isDesktop) {
          // Desktop applies on change rather than behind an Apply button, so
          // coalesce a burst of ticks into one request.
          clearTimeout(this.applyFiltersTimer);
          this.applyFiltersTimer = setTimeout(
            () => this.applyFilters(),
            FILTER_DEBOUNCE_MS,
          );
        } else {
          this.updateApplyButtonCount();
        }
      });
    });
  }

  toggleDrawer(type) {
    const drawer = type === "desktop" ? this.drawerDesktop : this.drawerMobile;
    if (!drawer) return;

    const isOpen = drawer.hasAttribute("open");
    isOpen ? drawer.hide() : drawer.show();
  }

  onDrawerToggle(type, isOpen) {
    const toggleBtn = this.querySelector(`[data-filter-toggle="${type}"]`);

    if (type === "desktop") {
      const textEl = toggleBtn?.querySelector(".filter-toggle-text");
      if (textEl) textEl.textContent = isOpen ? "Hide Filters" : "Show Filters";
      if (this.productGridWrapper) {
        isOpen
          ? this.productGridWrapper.classList.add(
              "fusion-collection-wrapper--with-drawer",
            )
          : this.productGridWrapper.classList.remove(
              "fusion-collection-wrapper--with-drawer",
            );
      }
    }
  }

  toggleAccordion(button) {
    const isExpanded = button.getAttribute("aria-expanded") === "true";
    const content = button.nextElementSibling;
    const chevron = button.querySelector(".filter-chevron");

    if (!content) return;

    button.setAttribute("aria-expanded", !isExpanded);
    content.classList.toggle("max-h-[2000px]", !isExpanded);
    content.classList.toggle("opacity-100", !isExpanded);
    content.classList.toggle("max-h-0", isExpanded);
    content.classList.toggle("opacity-0", isExpanded);
    chevron?.classList.toggle("rotate-180", !isExpanded);
  }

  openAccordionsWithActiveFilters() {
    this.querySelectorAll("[data-filter-type]").forEach((filterGroup) => {
      const hasActiveFilters =
        filterGroup.querySelectorAll("[data-filter-input]:checked").length > 0;
      const button = filterGroup.querySelector("[data-accordion-toggle]");

      if (
        hasActiveFilters &&
        button?.getAttribute("aria-expanded") === "false"
      ) {
        this.toggleAccordion(button);
      }
    });
  }

  updateApplyButtonCount() {
    const applyBtnText = this.drawerMobile?.querySelector("[data-apply-text]");
    if (!applyBtnText) return;

    const mobileForm = this.drawerMobile?.querySelector(
      '[data-filter-form="mobile"]',
    );
    const count = mobileForm
      ? mobileForm.querySelectorAll("[data-filter-input]:checked").length
      : 0;
    applyBtnText.textContent = count > 0 ? `Apply (${count})` : "Apply";
  }

  applyFilters() {
    // An explicit apply (Apply button, or the debounce firing) supersedes any
    // queued one.
    clearTimeout(this.applyFiltersTimer);

    const activeDrawer = this.isDesktop
      ? this.drawerDesktop
      : this.drawerMobile;
    const activeForm = activeDrawer?.querySelector(
      `[data-filter-form="${this.isDesktop ? "desktop" : "mobile"}"]`,
    );

    if (!activeForm) return;

    const formData = new FormData();
    const checkedInputs = activeForm.querySelectorAll(
      "[data-filter-input]:checked",
    );

    checkedInputs.forEach((input) => {
      formData.append(input.name, input.value);
    });

    // Include hidden inputs (search query, options, etc.)
    const hiddenInputs = activeForm.querySelectorAll('input[type="hidden"]');
    hiddenInputs.forEach((input) => {
      formData.append(input.name, input.value);
    });

    // Only include availability filter if there are other filters applied
    if (checkedInputs.length > 0) {
      formData.append("filter.v.availability", "1");
    }

    const sortSelect = this.querySelector("[data-sort-select]");
    if (sortSelect) {
      formData.append("sort_by", sortSelect.value);
    }

    const searchParams = new URLSearchParams(formData).toString();
    this.renderPage(searchParams, true);
  }

  setLoading(isLoading) {
    if (!this.productGridWrapper) return;

    if (isLoading) {
      this.productGridWrapper.setAttribute("data-filtering", "");
    } else {
      this.productGridWrapper.removeAttribute("data-filtering");
    }
  }

  renderPage(searchParams, updateURL = true) {
    const sectionId = this.productGrid?.dataset.id;
    if (!sectionId) return;

    // A newer request supersedes whatever is still in flight, otherwise a slow
    // earlier response can land last and overwrite the correct results.
    this.abortController?.abort();
    this.abortController = new AbortController();

    const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
    this.setLoading(true);

    fetch(url, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");

        this.updateElement("ProductGridContainer", html);
        this.updateElement("paginationOutput", html);
        this.updateElement('[data-filter-form="desktop"]', html);
        this.updateElement('[data-filter-form="mobile"]', html);

        this.attachFilterListeners();
        this.updateFilterCount();
        this.openAccordionsWithActiveFilters();

        this.setLoading(false);

        if (!this.isDesktop) this.drawerMobile?.hide();

        if (updateURL) {
          const newUrl = searchParams
            ? `${window.location.pathname}?${searchParams}`
            : window.location.pathname;
          history.pushState({ searchParams }, "", newUrl);
        }
      })
      .catch((error) => {
        // An aborted request has already been replaced by a newer one, which
        // owns the loading state from here.
        if (error?.name === "AbortError") return;
        this.setLoading(false);
      });
  }

  updateElement(selector, html) {
    const newEl = selector.startsWith("[")
      ? html.querySelector(selector)
      : html.getElementById(selector);
    const currentEl = selector.startsWith("[")
      ? document.querySelector(selector)
      : document.getElementById(selector);
    if (newEl && currentEl) currentEl.innerHTML = newEl.innerHTML;
  }

  handleSortChange(sortValue) {
    const url = new URL(window.location.href);
    url.searchParams.set("sort_by", sortValue);
    this.renderPage(url.searchParams.toString(), true);
  }

  clearAllFilters() {
    // Guard against a tick made moments before Clear All re-applying itself.
    clearTimeout(this.applyFiltersTimer);

    this.querySelectorAll("[data-filter-input]:checked").forEach((input) => {
      input.checked = false;
    });

    const sortSelect = this.querySelector("[data-sort-select]");
    if (sortSelect && sortSelect.options.length > 0) {
      sortSelect.selectedIndex = 0;
      this.setSelectDynamicWidth();
    }

    const url = new URL(window.location.href);
    const searchParams = new URLSearchParams();

    for (const [key, value] of url.searchParams.entries()) {
      if (!key.startsWith("filter.") && key !== "sort_by") {
        searchParams.set(key, value);
      }
    }

    this.renderPage(searchParams.toString(), true);
    if (!this.isDesktop) this.drawerMobile?.hide();
  }

  updateFilterCount() {
    const activeDrawer = this.isDesktop
      ? this.drawerDesktop
      : this.drawerMobile;
    const activeForm = activeDrawer?.querySelector(
      `[data-filter-form="${this.isDesktop ? "desktop" : "mobile"}"]`,
    );

    // Exclude availability filter from count
    const count = activeForm
      ? Array.from(
          activeForm.querySelectorAll("[data-filter-input]:checked"),
        ).filter((input) => !input.name.includes("availability")).length
      : 0;

    const countValues = this.querySelectorAll("[data-filter-count]");
    countValues.forEach((el) => (el.textContent = count));

    const countWrappers = this.querySelectorAll("[data-filter-count-wrapper]");
    countWrappers.forEach((wrapper) => {
      const wrapperType = wrapper.getAttribute("data-filter-count-wrapper");
      if (wrapperType === "mobile") {
        wrapper.classList.toggle("hidden", count === 0);
      } else if (wrapperType === "desktop") {
        if (count === 0) {
          wrapper.classList.remove("lg:flex");
        } else {
          wrapper.classList.add("lg:flex");
        }
      }
    });

    this.updateApplyButtonCount();
  }

  setSelectDynamicWidth() {
    const sortSelect = this.querySelector("[data-sort-select]");
    if (!sortSelect) return;

    const measureEl = document.createElement("span");
    measureEl.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-family: ${getComputedStyle(sortSelect).fontFamily};
      font-size: ${getComputedStyle(sortSelect).fontSize};
      font-weight: ${getComputedStyle(sortSelect).fontWeight};
    `;

    const selectedText =
      sortSelect.options[sortSelect.selectedIndex]?.text || "";
    measureEl.textContent = selectedText;

    document.body.appendChild(measureEl);
    const textWidth = measureEl.offsetWidth;
    document.body.removeChild(measureEl);

    sortSelect.style.setProperty("--select-width", `${textWidth + 24}px`);

    const sortControl = this.querySelector("[data-sort-control]");
    if (sortControl) {
      sortControl.classList.remove("opacity-0");
      sortControl.classList.add("opacity-100");
    }
  }
}

customElements.define("filter-drawer", FilterDrawer);
